import { FC } from 'react';
import { Document, Page } from 'react-pdf';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import MarkdownContent from '@/components/ui/MarkdownContent/MarkdownContent';

interface ParsedQuestionContent {
  hasExtract: boolean;
  questionNumber: string;
  extractInstruction: string;
  extractText: string;
  mainQuestion: string;
  referencedLines: { start: number; end: number } | null;
}

interface QuestionContentAreaProps {
  questionContent: string;
  marks: number;
  imagePageNumbers?: number[] | null;
  pdfUrl?: string;
  readingInsertContent?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  parseQuestionContent: (content: string) => ParsedQuestionContent;
}

const QuestionContentArea: FC<QuestionContentAreaProps> = ({
  questionContent,
  marks,
  imagePageNumbers,
  pdfUrl,
  isCollapsed,
  onToggleCollapse,
  parseQuestionContent,
}) => {
  const parsed = parseQuestionContent(questionContent || '');

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden transition-all flex-shrink-0">
      <div className={`p-6 ${isCollapsed ? '' : 'max-h-[60vh] overflow-y-auto'} flex flex-col gap-6 relative`}>
        {/* Collapse/Expand Button */}
        <button
          onClick={onToggleCollapse}
          className="absolute top-4 right-4 flex items-center gap-2 z-10"
          title={isCollapsed ? 'Expand question' : 'Collapse question'}
        >
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
            {isCollapsed ? 'Click to expand' : 'Click to collapse'}
          </span>
          <div className="w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors">
            {isCollapsed ? <ChevronDownIcon className="w-10 h-10" /> : <ChevronUpIcon className="w-10 h-10" />}
          </div>
        </button>

        {/* Question Text */}
        <div className="flex flex-col gap-4 pr-14">
          {isCollapsed ? (
            <div className="text-base leading-relaxed line-clamp-2">
              <MarkdownContent content={questionContent} />
            </div>
          ) : (
            <>
              {/* Question instruction (if extract exists) */}
              {parsed.hasExtract && (
                <div className="flex flex-col gap-2">
                  <div className="text-base">
                    <MarkdownContent content={parsed.extractInstruction} />
                  </div>
                </div>
              )}

              {/* Extract in bordered box */}
              {parsed.hasExtract && parsed.extractText && (
                <div className="border-2 border-gray-900 rounded-lg p-6 bg-[#ffe0b2]">
                  <div className="text-base leading-relaxed">
                    <MarkdownContent content={parsed.extractText} />
                  </div>
                </div>
              )}

              {/* Main question text */}
              <div className="text-base leading-relaxed text-lg">
                <MarkdownContent content={parsed.mainQuestion} />
              </div>

              {/* Marks in bottom left corner */}
              <span className="text-sm font-bold text-gray-600 self-start">[{marks} marks]</span>
            </>
          )}
        </div>

        {/* Display images if question has them - Hide when collapsed */}
        {!isCollapsed && imagePageNumbers && imagePageNumbers.length > 0 && pdfUrl && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-gray-600">Reference Image(s):</p>
            <Document file={pdfUrl}>
              {imagePageNumbers.map((pageNum: number) => (
                <div key={pageNum} className="mb-4">
                  <Page
                    pageNumber={pageNum}
                    width={Math.min(window.innerWidth * 0.6, 600)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="shadow-md"
                  />
                </div>
              ))}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionContentArea;
